import { Link, useLocation } from "react-router-dom";
import { Menu } from "antd";
import {
  DesktopOutlined,
  LogoutOutlined,
  UserOutlined,
  SettingOutlined,
  CalendarOutlined,
  SettingTwoTone,
  RobotOutlined,
  ApartmentOutlined,
  BankOutlined,
  ContactsOutlined,
  CarOutlined,
  PlayCircleOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useAuth } from "shared/contexts/AuthProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { usePageTitle } from "shared/contexts/PageTitleContext";
import { hasAnyOrgRole } from "shared/utils/orgRoles";
import { ROBOT_LOGS_REQUIRED_ORG_ROLES } from "features/robot-logs/utils/logConstants";
import { Helmet } from "react-helmet-async";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const MenuBar = () => {
  const { t, ready: translationReady } = useTranslation();

  const translate = (key) => {
    return translationReady ? t(key) : key;
  };

  const { auth, isAuthenticated, user, isSuperUser, loading, logout } =
    useAuth();
  const { hasRoleInSelectedOrg } = useUserOrg();

  // Robot Log Data is only offered to log managers of the currently selected
  // organization (superusers always see it). Same policy as the route guard.
  const canViewRobotLogs = hasAnyOrgRole(
    { isSuperUser, hasRoleInSelectedOrg },
    ROBOT_LOGS_REQUIRED_ORG_ROLES,
  );

  const { pageTitle, updatePageTitle } = usePageTitle();

  const location = useLocation();

  const menuItems = useMemo(() => {
    // Don't render menu items until translations are loaded
    if (!translationReady) return [];
    const baseMenuItems = [
      {
        key: "/dashboard",
        label: <Link to="/dashboard">{translate("navigation.robots")}</Link>,
        icon: <RobotOutlined />,
        title: translate("navigation.home"),
      },
      // {
      //   key: "/monitor-wall",
      //   label: (
      //     <Link to="/monitor-wall">{translate("navigation.monitorWall")}</Link>
      //   ),
      //   icon: <DesktopOutlined />,
      //   title: translate("navigation.monitorWall"),
      // },
      {
        key: "/mcap-player",
        label: <Link to="/mcap-player">MCAP Player</Link>,
        icon: <PlayCircleOutlined />,
        title: "MCAP File Player",
      },
    ];

    if (canViewRobotLogs) {
      baseMenuItems.push({
        key: "/robot-logs",
        label: <Link to="/robot-logs">Robot Log Data</Link>,
        icon: <BarChartOutlined />,
        title: "Robot Log Data",
      });
    }

    const menuItemMyPages = [
      {
        key: "mypages/",
        label: translate("navigation.myRobots"),
        icon: <CalendarOutlined />, // Changed to a "My personal space" icon
        title: translate("navigation.myRobots"),
        children: [
          // {
          //   key: "/my/profile",
          //   label: <Link to="/my/profile">My Profile</Link>,
          //   icon: <SettingOutlined />,
          // },
          {
            key: "/organization",
            label: (
              <Link to="/organization">
                {translate("navigation.organization")}
              </Link>
            ),
            icon: <BankOutlined />,
            title: translate("navigation.organization"),
          },
          {
            key: "/my/orgs",
            label: (
              <Link to="/my/orgs">
                {translate("navigation.myOrganizations")}
              </Link>
            ),
            icon: <ApartmentOutlined />,
            title: translate("navigation.myOrganizations"),
          },
          {
            key: "/my/robots",
            label: (
              <Link to="/my/robots">{translate("navigation.myRobots")}</Link>
            ),
            icon: <CarOutlined />,
            title: translate("navigation.myRobots"),
          },
        ],
      },
    ];

    const menuItemsSuperAdmin = [
      {
        key: "administrations/",
        label: translate("navigation.administration"),
        icon: <SettingTwoTone />,
        title: translate("navigation.administration"),
        children: [
          {
            key: "/administrations/users",
            label: (
              <Link to="/administrations/users">
                {translate("administration.users")}
              </Link>
            ),
            icon: <ContactsOutlined />,
            title: translate("administration.users"),
          },
          {
            key: "/administrations/robots",
            label: (
              <Link to="/administrations/robots">
                {translate("navigation.robots")}
              </Link>
            ),
            icon: <CarOutlined />,
            title: translate("administration.robotManagement"),
          },
          {
            key: "/administrations/robot-types",
            label: <Link to="/administrations/robot-types">Robot Types</Link>,
            icon: <RobotOutlined />,
            title: "Robot Types Management",
          },
          {
            key: "/administrations/orgs",
            label: (
              <Link to="/administrations/orgs">
                {translate("administration.organizations")}
              </Link>
            ),
            icon: <ApartmentOutlined />,
            title: translate("administration.organizations"),
          },
          {
            key: "/administrations/dashboard",
            label: (
              <Link to="/administrations/dashboard">
                {translate("navigation.dashboard")}
              </Link>
            ),
            icon: <DesktopOutlined />,
            title: translate("navigation.dashboard"),
          },
        ],
      },
    ];

    const menuItemUserLogin = [
      {
        key: "/login",
        icon: <UserOutlined />, // Changed to a "User Login" icon
        label: <Link to="/login">{translate("common.login")}</Link>,
        title: translate("common.login"),
      },
    ];

    const menuItemUserLogout = [
      {
        key: "/my",
        icon: <UserOutlined />,
        label: user?.username ? `Hi, ${user.username}` : "Hi, User",
        children: [
          {
            key: "/my/profile",
            label: (
              <Link to="/my/profile">{translate("navigation.profile")}</Link>
            ),
            icon: <SettingOutlined />,
            title: translate("navigation.profile"),
          },
          {
            key: "/logout",
            label: translate("common.logout"),
            icon: <LogoutOutlined />,
            title: translate("common.logout"),
            onClick: () => {
              // Handle logout logic here
              logout();
            },
          },
        ],
      },
    ];

    // Build final menu items
    const finalMenuItems = [...baseMenuItems];

    if (isAuthenticated && user) {
      finalMenuItems.push(...menuItemMyPages);
    }

    if (isSuperUser) {
      finalMenuItems.push(...menuItemsSuperAdmin);
    }

    if (!isAuthenticated) {
      finalMenuItems.push(...menuItemUserLogin);
    } else {
      finalMenuItems.push(...menuItemUserLogout);
    }

    return finalMenuItems;
  }, [
    isAuthenticated,
    user,
    isSuperUser,
    canViewRobotLogs,
    logout,
    translate,
    translationReady,
  ]);

  // console.log("current pathname: ", location.pathname);

  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    const selectedKeys = [];

    menuItems.forEach((item) => {
      if (currentPath === item.key) {
        selectedKeys.push(item.key);
      }
      if (item.children) {
        item.children.forEach((child) => {
          // Use exact match or startsWith for better precision
          if (
            currentPath === child.key ||
            currentPath.startsWith(child.key + "/")
          ) {
            selectedKeys.push(child.key);
          }
        });
      }
    });

    return selectedKeys;
  };

  const getSelectedPageTitle = useCallback(() => {
    const currentPath = location.pathname;

    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (
            currentPath === child.key ||
            currentPath.startsWith(child.key + "/")
          ) {
            return (child.title || child.label) + " - Rubberneck";
          }
        }
      }

      if (currentPath === item.key || currentPath.startsWith(item.key + "/")) {
        return (item.title || item.label) + " - Rubberneck";
      }
    }
    if (currentPath === "/") {
      return "Home - Rubberneck";
    }
    return null;
  }, [menuItems, location.pathname]);

  const currentPageTitle = useMemo(
    () => getSelectedPageTitle(),
    [menuItems, location.pathname],
  );

  useEffect(() => {
    if (loading || !currentPageTitle) return; // Wait until loading is false
    updatePageTitle(currentPageTitle);
  }, [currentPageTitle, updatePageTitle, loading]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {!translationReady ? null : (
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          items={menuItems}
          defaultOpenKeys={["administrations/", "mypages/"]}
        ></Menu>
      )}
    </>
  );
};

export default MenuBar;
